import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

/**
 * MatchSnippetsTicker — a marquee ticker pinned above the fold on Home.
 *
 * Data source: GET /api/clubvip/highlights returns { items: [...] }
 * Each item is either:
 *   { kind: "match_group", count, event_title, event_city, event_id }
 *   { kind: "static",     text }
 *
 * Uses the .marquee / .marquee-track primitive already defined in index.css,
 * duplicating the payload once so the animation loops seamlessly.
 */
export default function MatchSnippetsTicker() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancel = false;
    api.get("/clubvip/highlights").then(({ data }) => {
      if (cancel) return;
      const arr = Array.isArray(data?.items) ? data.items : [];
      // Ensure we always have enough to feel alive — pad from defaults if needed.
      setItems(arr.length >= 3 ? arr : [
        ...arr,
        { kind: "static", text: "India's first AI-native event ecosystem" },
        { kind: "static", text: "Every Match profile is admin-verified" },
        { kind: "static", text: "Match works for RSVPs too — no VIP gate" },
      ].slice(0, 6));
    }).catch(() => {
      setItems([
        { kind: "static", text: "India's first AI-native event ecosystem" },
        { kind: "static", text: "Every Match profile is admin-verified" },
        { kind: "static", text: "Match works for RSVPs too — no VIP gate" },
        { kind: "static", text: "Genie plans your night in 10 seconds" },
      ]);
    });
    return () => { cancel = true; };
  }, []);

  if (items.length === 0) return null;
  const doubled = [...items, ...items]; // seamless loop

  const renderItem = (it, i) => {
    if (it.kind === "match_group" && it.event_id) {
      return (
        <Link
          key={`m-${it.event_id}-${i}`}
          to={`/clubvip?event=${it.event_id}`}
          data-testid={`match-ticker-item-${i}`}
          className="inline-flex items-center gap-2 text-[12px] whitespace-nowrap group"
        >
          <Heart className="w-3.5 h-3.5 text-magenta group-hover:scale-110 transition-transform" />
          <span className="text-lime font-bold">{it.count} fans matched</span>
          <span className="text-white/70">at</span>
          <span className="font-heading font-bold text-white group-hover:underline">{it.event_title}</span>
          {it.event_city && <span className="text-white/50">· {it.event_city}</span>}
        </Link>
      );
    }
    return (
      <span
        key={`s-${i}`}
        data-testid={`match-ticker-item-${i}`}
        className="inline-flex items-center gap-2 text-[12px] text-white/85 whitespace-nowrap"
      >
        <Sparkles className="w-3 h-3 text-skyx" />
        {it.text}
      </span>
    );
  };

  return (
    <div
      data-testid="match-snippets-ticker"
      className="border-y border-white/10 relative overflow-hidden"
      style={{ background: "linear-gradient(90deg, rgba(204,255,0,0.06), rgba(255,0,127,0.06), rgba(0,229,255,0.06))" }}
    >
      <div className="max-w-7xl mx-auto flex items-stretch">
        <span className="hidden sm:flex shrink-0 items-center gap-1.5 px-4 border-r border-white/10 text-[10px] font-mono uppercase tracking-widest text-lime bg-black/40">
          <span className="w-1.5 h-1.5 rounded-full bg-lime animate-pulse" />
          Live
        </span>
        <div className="marquee flex-1 py-2.5">
          <div className="marquee-track">
            {doubled.map(renderItem)}
          </div>
        </div>
      </div>
    </div>
  );
}
